import { getAuthToken } from "@/services/auth.service";
import {
  apiAverageProcessTime,
  apiFetchAthletesTotalByDiscipline,
  apiFetchDisciplinesAndCountries,
  apiFetchEnvelopesTotal,
  apiFetchEnvelopeTotalByStatus,
  apiFetchErrorsResolutionTime,
  apiFetchErrorsTotal,
  apiFetchErrorsTotalByStatus,
  apiFetchFileByProcessType,
  apiFetchFilesTotal,
  apiFetchFilesTotalByDescription,
  apiFetchFilesTotalByStatus,
} from "@/services/stats.service";
import {
  type errorsStat,
  type numberedStat,
  type objectByStatus,
  type StatsFilter,
} from "@/types/stats.types";
import { serverUrls } from "@/utils/constants";
import { errorHandler } from "@/utils/errorHandler";
import { defineStore } from "pinia";

export const useStatsStore = defineStore("statsStore", {
  state: () => ({
    envelopes: 0 as number,
    files: 0 as number,
    averageProcessTime: '' as string,

    errors: {} as errorsStat,

    envelopesByStatus: {} as objectByStatus,
    filesByStatus: {} as objectByStatus,
    athleteByDiscipline: {} as objectByStatus,
    filesByDescription: {} as objectByStatus,
    errorsByStatus: {} as objectByStatus,
    processType: {} as objectByStatus,

    errorsResolutionTime: {} as numberedStat,
    countries: [] as string[],
    disciplines: [] as string[],

    envelopesByStatusColors: {
      AWAITING_SCAN: "#E17359",
      AWAITING_FILES: "#F5A623",
      RECEIVING_FILES: "#FFD700",
      MANUAL_INTERVENTION: "#FFA500",
      GENERATING: "#D224A1",
      VALIDATION_PENDING: "#099B66",
      VALIDATING: "#00B894",
      EDITING: "#8B3CB9",
      COMPLETED: "#325AFF",
      DUPLICATE: "#DF3030",
    } as Record<string, string>,
    filesByStatusColors: {
      ASSOCIATION: "#E17359",
      GENERATION: "#D224A1",
      VALIDATION: "#099B66",
      EDITION: "#8B3CB9",
      PRODUCTION: "#325AFF",
      ERROR: "#DF3030",
      NOBODY_RECOGNIZED: "#F5A623",
      NOBODY_ON_IMAGE: "#FFA500",
      DISCARDED: "#CCCCCC",
    } as Record<string, string>,
  }),
  actions: {
    async fetchStats(filter: StatsFilter) {
      try {
        const promises = {
          envelopes: apiFetchEnvelopesTotal(filter),
          envelopesByStatus: apiFetchEnvelopeTotalByStatus(filter),
          filesByStatus: apiFetchFilesTotalByStatus(filter),
          filesByDescription: apiFetchFilesTotalByDescription(filter),
          athleteByDiscipline: apiFetchAthletesTotalByDiscipline(filter),
          files: apiFetchFilesTotal(filter),
          errorsByStatus: apiFetchErrorsTotalByStatus(filter),
          errorsResolutionTime: apiFetchErrorsResolutionTime(),
          errors: apiFetchErrorsTotal(filter),
          averageProcessTime: apiAverageProcessTime(),
          processType: apiFetchFileByProcessType(filter),
        };

        const results = await Promise.allSettled(
          Object.entries(promises).map(([key, p]) =>
            p.then((data) => ({ key, data }))
          )
        );

        for (const result of results) {
          if (result.status === "fulfilled") {
            const { key, data } = result.value;
            if (data !== null && data !== undefined) {
              (this as any)[key] = data;
            }
          } else {
            throw result.reason
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    async fetchDisciplinesAndCountries() {
      const response = await apiFetchDisciplinesAndCountries();
      if (response) {
        this.countries = response.countries;
        this.disciplines = response.disciplines;
      }
    },
    async exportStats() {
      try {
        const token = getAuthToken();
        const headers: HeadersInit = {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        const response: Response = await fetch(
          serverUrls.authUrl + "/stats/export/csv",
          {
            method: "GET",
            headers,
          }
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          let fileName = "export.csv";
          const disposition = response.headers.get("Content-Disposition");
          
          if ( disposition?.includes("filename=")) {
            const filenameRegex = /filename="?([^"]+)"?/;
            const match = filenameRegex.exec(disposition);
            if (match?.[1]) {
              fileName = match[1];
            }
          }
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          console.error("Erreur lors de l'export :", response.status);
        }
      } catch (error) {
                if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },
  },
});
